import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const STATUS_IDLE = 'idle';
const STATUS_REFRESHING = 'refreshing';
const STATUS_SUCCESS = 'success';
const STATUS_ERROR = 'error';

const SUCCESS_FEEDBACK_MS = 2000;
const ERROR_FEEDBACK_MS = 3000;

/**
 * Fixed bottom-right floating control for manual page refresh.
 * Portaled to document.body so it stays above Order Details / modal mode
 * (app modals use z-index ~1000000).
 * Calls the page's existing onRefresh handler — does not implement refresh itself.
 */
const FloatingRefreshButton = ({
    onRefresh,
    ariaLabel = 'Refresh',
    hoverLabel = 'Refresh',
    successLabel = 'Updated',
    errorLabel = 'Failed',
}) => {
    const [status, setStatus] = useState(STATUS_IDLE);
    const [hovered, setHovered] = useState(false);
    const inFlightRef = useRef(false);
    const feedbackTimerRef = useRef(null);
    const onRefreshRef = useRef(onRefresh);

    onRefreshRef.current = onRefresh;

    useEffect(() => () => {
        if (feedbackTimerRef.current) {
            clearTimeout(feedbackTimerRef.current);
        }
    }, []);

    const handleClick = useCallback(async () => {
        if (inFlightRef.current || typeof onRefreshRef.current !== 'function') return;

        inFlightRef.current = true;
        if (feedbackTimerRef.current) {
            clearTimeout(feedbackTimerRef.current);
            feedbackTimerRef.current = null;
        }
        setStatus(STATUS_REFRESHING);

        try {
            await onRefreshRef.current();
            setStatus(STATUS_SUCCESS);
            feedbackTimerRef.current = setTimeout(() => {
                setStatus(STATUS_IDLE);
                feedbackTimerRef.current = null;
            }, SUCCESS_FEEDBACK_MS);
        } catch (error) {
            console.error('Floating refresh failed:', error);
            setStatus(STATUS_ERROR);
            feedbackTimerRef.current = setTimeout(() => {
                setStatus(STATUS_IDLE);
                feedbackTimerRef.current = null;
            }, ERROR_FEEDBACK_MS);
        } finally {
            inFlightRef.current = false;
        }
    }, []);

    const isBusy = status === STATUS_REFRESHING;
    const showHoverLabel = hovered && status === STATUS_IDLE;
    const bubbleText =
        status === STATUS_SUCCESS
            ? successLabel
            : status === STATUS_ERROR
                ? errorLabel
                : showHoverLabel
                    ? hoverLabel
                    : null;

    const statusClass =
        status === STATUS_REFRESHING
            ? 'is-refreshing'
            : status === STATUS_SUCCESS
                ? 'is-success'
                : status === STATUS_ERROR
                    ? 'is-error'
                    : '';

    const control = (
        <div
            className={`floating-refresh-control ${statusClass}`}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            {bubbleText && (
                <span className="floating-refresh-label" role="status" aria-live="polite">
                    {bubbleText}
                </span>
            )}
            <button
                type="button"
                className="floating-refresh-btn"
                onClick={handleClick}
                disabled={isBusy}
                title={ariaLabel}
                aria-label={ariaLabel}
                aria-busy={isBusy}
            >
                {status === STATUS_SUCCESS ? (
                    <i className="fa fa-check" aria-hidden="true"></i>
                ) : status === STATUS_ERROR ? (
                    <i className="fa fa-exclamation" aria-hidden="true"></i>
                ) : (
                    <i
                        className={`fa fa-refresh ${isBusy ? 'fa-spin' : ''}`}
                        aria-hidden="true"
                    ></i>
                )}
            </button>
        </div>
    );

    if (typeof document === 'undefined') {
        return null;
    }

    return createPortal(control, document.body);
};

export default FloatingRefreshButton;
